
import React from 'react';
import { motion } from 'motion/react';
import { SimulationConfig, SpeedIncreaseMode, BallGrowthMode, ContainerShrinkMode } from './types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { SETTING_INFO } from './settingInfo';
import { CollapsibleSection } from './CollapsibleSection';
import { Toggle } from './Toggle';

interface PhysicsSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const PhysicsSettingsSection: React.FC<PhysicsSettingsSectionProps> = ({ config, setConfig }) => {
    const speedModes: SpeedIncreaseMode[] = ['STATIC', 'TIME', 'COLLISION'];
    const growthModes: BallGrowthMode[] = ['NONE', 'TIME', 'COLLISION', 'PLAYLIST_SYNC'];
    const shrinkModes: ContainerShrinkMode[] = ['NONE', 'TIME', 'COLLISION'];

    const isRhythmic = config.mode === 'RHYTHMIC';
    const rhythmicMsg = "This engine is specifically designed for RHYTHMIC mode.";

    return (
      <div className="flex flex-col gap-2">
        <CollapsibleSection title="Physics & Dynamics" icon="fa-bolt" darkMode={config.darkMode} defaultOpen>
            <div className={`w-full flex flex-col gap-4 p-5 rounded-3xl border ${config.darkMode ? 'bg-zinc-500/5 border-white/5 shadow-sm' : 'bg-zinc-50 border-neutral-100 shadow-sm'} mb-4`}>
                <SmartInput 
                    label="Gravity" 
                    min={0} max={0.5} step={0.001} 
                    value={config.gravity} 
                    onChange={v => setConfig(p => ({...p, gravity: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="gravity"
                />
                <SmartInput 
                    label="Bounce Power" 
                    min={0} max={2.0} step={0.01} 
                    value={config.restitution} 
                    onChange={v => setConfig(p => ({...p, restitution: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="restitution"
                />
                <SmartInput 
                    label="Ball Mass" 
                    min={0.1} max={10} step={0.1} 
                    value={config.ballMass} 
                    onChange={v => setConfig(p => ({...p, ballMass: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="ballMass"
                />
            </div>

            <div className={`w-full flex flex-col gap-4 p-5 rounded-[2.5rem] border ${config.darkMode ? 'bg-zinc-500/5 border-white/5' : 'bg-zinc-50 border-neutral-100 shadow-sm'} mb-4`}>
                <ControlGroup label="Weight Growth" settingKey="ballWeightGrowthEnabled" darkMode={config.darkMode}>
                    <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                        <Toggle active={config.ballWeightGrowthEnabled} onClick={() => setConfig(p => ({...p, ballWeightGrowthEnabled: !p.ballWeightGrowthEnabled}))} />
                    </div>
                </ControlGroup>
                {config.ballWeightGrowthEnabled && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className={`flex flex-col gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}
                    >
                        <SmartInput label="Min Weight" min={0.1} max={100} step={0.1} value={config.ballMinWeight} onChange={v => setConfig(p => ({...p, ballMinWeight: v}))} darkMode={config.darkMode} settingKey="ballMinWeight" />
                        <SmartInput label="Max Weight" min={0.1} max={100} step={0.1} value={config.ballMaxWeight} onChange={v => setConfig(p => ({...p, ballMaxWeight: v}))} darkMode={config.darkMode} settingKey="ballMaxWeight" />
                        <SmartInput label="Growth Time (s)" min={1} max={300} step={1} value={config.ballWeightGrowthTime} onChange={v => setConfig(p => ({...p, ballWeightGrowthTime: v}))} darkMode={config.darkMode} settingKey="ballWeightGrowthTime" />
                    </motion.div>
                )}
            </div>
        </CollapsibleSection>

        {/* RHYTHMIC SYNC ENGINE (NEW) */}
        <CollapsibleSection 
          title="Rhythm Sync Engine"
          icon="fa-clock" 
          darkMode={config.darkMode} 
          defaultOpen={isRhythmic}
        >
            <div className={`w-full flex flex-col gap-5 p-5 rounded-3xl border ${config.darkMode ? 'bg-pink-500/5 border-pink-500/10 shadow-sm' : 'bg-pink-50 border-pink-100 shadow-sm'} mb-4 transition-all duration-300 ${!isRhythmic ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
                <span className="text-[11px] font-black uppercase text-pink-500 opacity-80 mb-2 block">Visualizer Tuning</span>
                
                <SmartInput 
                    label="Song Speed (Time Stretch)" 
                    min={0.1} max={5.0} step={0.1} 
                    value={config.rhythmicTimeScale || 1.0} 
                    onChange={v => setConfig(p => ({...p, rhythmicTimeScale: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicTimeScale"
                    disabled={!isRhythmic}
                    disabledMessage={rhythmicMsg}
                    modes={['RHYTHMIC']}
                />

                <ControlGroup 
                label="Acceleration Mode" 
                settingKey="rhythmicTimeScaleIncreaseMode"
                disabled={!isRhythmic}
                disabledMessage={rhythmicMsg}
                modes={['RHYTHMIC']}
                darkMode={config.darkMode}
                >
                    <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                        <select 
                            value={config.rhythmicTimeScaleIncreaseMode || 'NONE'} 
                            onChange={e => setConfig(p => ({...p, rhythmicTimeScaleIncreaseMode: e.target.value as any}))}
                            className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                        >
                            <option value="NONE" className="bg-neutral-900 text-white">NONE</option>
                            <option value="TIME" className="bg-neutral-900 text-white">TIME</option>
                            <option value="COLLISION" className="bg-neutral-900 text-white">COLLISION</option>
                        </select>
                    </div>
                </ControlGroup>

                <div className="flex flex-col gap-4">
                    <SmartInput 
                        label="Acceleration Rate" 
                        min={0} max={0.5} step={0.001} 
                        value={config.rhythmicTimeScaleRate || 0.01} 
                        onChange={v => setConfig(p => ({...p, rhythmicTimeScaleRate: v}))} 
                        darkMode={config.darkMode} 
                        settingKey="rhythmicTimeScaleRate"
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />
                    <SmartInput 
                        label="Acceleration Factor" 
                        min={-0.1} max={0.1} step={0.0001} 
                        value={config.rhythmicTimeScaleAcceleration || 0} 
                        onChange={v => setConfig(p => ({...p, rhythmicTimeScaleAcceleration: v}))} 
                        darkMode={config.darkMode} 
                        settingKey="rhythmicTimeScaleAcceleration"
                        disabled={!isRhythmic}
                        disabledMessage={rhythmicMsg}
                        modes={['RHYTHMIC']}
                    />
                </div>
                
                <SmartInput 
                    label="Circle Size Multiplier" 
                    min={0.5} max={5.0} step={0.1} 
                    value={config.rhythmicCircleScale || 1.0} 
                    onChange={v => setConfig(p => ({...p, rhythmicCircleScale: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="rhythmicCircleScale"
                    disabled={!isRhythmic}
                    disabledMessage={rhythmicMsg}
                    modes={['RHYTHMIC']}
                />
                <p className="text-[10px] font-bold opacity-40 text-center leading-normal">
                    Higher values expand the orbit range.<br/>The sphere compensates velocity for rhythm sync.
                </p>
            </div>
        </CollapsibleSection>

        {/* DYNAMICS: SPEED & GROWTH */}
        <CollapsibleSection title="Dynamics Engine" icon="fa-chart-line" darkMode={config.darkMode}>
            <div className={`w-full flex flex-col gap-6 p-5 rounded-3xl border ${config.darkMode ? 'bg-blue-500/5 border-blue-500/10 shadow-sm' : 'bg-blue-50 border-blue-100 shadow-sm'} mb-4`}>
                
                {/* SPEED CONTROLS */}
                <div className="flex flex-col gap-4">
                    <span className="text-[11px] font-black uppercase text-blue-500 opacity-80 mb-2 block">Global Speed Engine</span>
                    <ControlGroup label="Increase Mode" settingKey="speedIncreaseMode" darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <select 
                                value={config.speedIncreaseMode} 
                                onChange={e => setConfig(p => ({...p, speedIncreaseMode: e.target.value as SpeedIncreaseMode}))}
                                className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                            >
                                {speedModes.map(m => <option key={m} value={m} className="bg-neutral-900 text-white">{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    <div className={`grid grid-cols-1 gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}>
                        <SmartInput label="Initial Speed" min={0.1} max={10.0} step={0.1} value={config.initialSpeed} onChange={v => setConfig(p => ({...p, initialSpeed: v}))} darkMode={config.darkMode} settingKey="initialSpeed" />
                        <SmartInput label="Increment Rate" min={0} max={0.1} step={0.0001} value={config.speedIncreaseRate} onChange={v => setConfig(p => ({...p, speedIncreaseRate: v}))} darkMode={config.darkMode} settingKey="speedIncreaseRate" />
                        <SmartInput label="Acceleration" min={-0.1} max={0.1} step={0.0001} value={config.speedAcceleration} onChange={v => setConfig(p => ({...p, speedAcceleration: v}))} darkMode={config.darkMode} settingKey="speedAcceleration" />
                    </div>
                    <SmartInput label="Master Velocity" min={0.1} max={5.0} step={0.1} value={config.masterSpeed} onChange={v => setConfig(p => ({...p, masterSpeed: v}))} darkMode={config.darkMode} settingKey="masterSpeed" />
                </div>

                <div className="h-px bg-white/5 w-full" />

                {/* BALL GROWTH CONTROLS */}
                <div className="flex flex-col gap-4">
                    <span className="text-[11px] font-black uppercase text-purple-500 opacity-80 mb-2 block">Sphere Expansion</span>
                    <ControlGroup label="Growth Mode" settingKey="ballGrowthMode" darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <select 
                                value={config.ballGrowthMode} 
                                onChange={e => setConfig(p => ({...p, ballGrowthMode: e.target.value as BallGrowthMode}))}
                                className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                            >
                                {growthModes.map(m => <option key={m} value={m} className="bg-neutral-900 text-white">{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    <div className={`grid grid-cols-1 gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}>
                        <SmartInput label="Growth Rate" min={0.01} max={5} step={0.01} value={config.ballGrowthRate} onChange={v => setConfig(p => ({...p, ballGrowthRate: v}))} darkMode={config.darkMode} settingKey="ballGrowthRate" />
                        <SmartInput label="Acceleration" min={-0.1} max={0.1} step={0.0001} value={config.ballGrowthAcceleration} onChange={v => setConfig(p => ({...p, ballGrowthAcceleration: v}))} darkMode={config.darkMode} settingKey="ballGrowthAcceleration" />
                        <SmartInput label="Radius Limit" min={10} max={300} value={config.ballMaxRadius} onChange={v => setConfig(p => ({...p, ballMaxRadius: v}))} darkMode={config.darkMode} settingKey="ballMaxRadius" />
                    </div>
                </div>

                <div className="h-px bg-white/5 w-full" />

                {/* CONTAINER SHRINK CONTROLS */}
                <div className="flex flex-col gap-4">
                    <span className="text-[11px] font-black uppercase text-emerald-500 opacity-80 mb-2 block">Container Contraction</span>
                    <ControlGroup label="Shrink Mode" settingKey="containerShrinkMode" darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <select 
                                value={config.containerShrinkMode} 
                                onChange={e => setConfig(p => ({...p, containerShrinkMode: e.target.value as ContainerShrinkMode}))}
                                className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                            >
                                {shrinkModes.map(m => <option key={m} value={m} className="bg-neutral-900 text-white">{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    <div className={`grid grid-cols-1 gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}>
                        <SmartInput label="Shrink Rate" min={0.1} max={5} step={0.1} value={config.containerShrinkRate} onChange={v => setConfig(p => ({...p, containerShrinkRate: v}))} darkMode={config.darkMode} settingKey="containerShrinkRate" />
                        <SmartInput label="Acceleration" min={-0.1} max={0.1} step={0.0001} value={config.containerShrinkAcceleration} onChange={v => setConfig(p => ({...p, containerShrinkAcceleration: v}))} darkMode={config.darkMode} settingKey="containerShrinkAcceleration" />
                        <SmartInput label="Radius Minimum" min={0} max={200} value={config.minContainerRadius} onChange={v => setConfig(p => ({...p, minContainerRadius: v}))} darkMode={config.darkMode} settingKey="minContainerRadius" />
                    </div>
                </div>

            </div>
        </CollapsibleSection>
      </div>
    );
};
